import React, { useRef } from 'react';

import {
	ActionIcon,
	Box,
	Button,
	FileButton,
	Flex,
	Icon,
	Loader,
	Stack,
	TFNotifier,
	TFText,
	Tooltip,
} from '@tf/ui';
import { fmt } from '@tf/utils';

import { isPreviewableFile } from '@/core/api/files';
import {
	downloadAttachedFile,
	getAttachedFileBlob,
	useCreateDetailsAttachmentMutation,
	useFilesInfo,
	useRemoveDetailsAttachmentMutation,
	useUpdateDetailsAttachmentMutation,
} from '@/core/api/payments';

interface Props {
	additionalDocuments?: { _object: string }[];
	detailsId?: string;
	paymentId: string;
	paymentKind: 'outward' | 'inward';
	decisionWasMade: boolean;
}

const PaymentPreviewButton: React.FC<{ fileId: string; fileName: string }> = ({ fileId, fileName }) => {
	const handlePreview = React.useCallback(async () => {
		try {
			const blob = await getAttachedFileBlob(fileId);
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			URL.revokeObjectURL(url);
		} catch (e) {
			console.error('Preview failed:', e);
			TFNotifier.error('File preview failed', {
				id: 'PaymentPreviewError',
			});
		}
	}, [fileId]);

	return (
		<Tooltip label={`Preview ${fileName}`} withArrow>
			<ActionIcon variant="transparent" color="blue">
				<Icon.IconEye size={22} stroke={1.5} onClick={handlePreview} />
			</ActionIcon>
		</Tooltip>
	);
};

export const AdditionalDocuments: React.FC<Props> = ({
	additionalDocuments,
	detailsId,
	paymentId,
	paymentKind,
	decisionWasMade,
}) => {
	const resetFileRef = useRef<() => void>(null);

	const additionalDocumentsQueryResult = useFilesInfo(additionalDocuments?.map((a) => a._object) ?? []);

	const createDetailsAttachmentMutation = useCreateDetailsAttachmentMutation(paymentKind);
	const updateDetailsAttachmentMutation = useUpdateDetailsAttachmentMutation();
	const removeDetailsAttachmentMutation = useRemoveDetailsAttachmentMutation();

	return (
		<>
			{Boolean(additionalDocuments?.length) && (
				<Box mt="32px">
					<TFText fz="16px" lh="18px" fw="600">
						Additional documents
					</TFText>
					{additionalDocumentsQueryResult.map(({ data, isPending }, idx) => {
						const canPreview = data ? isPreviewableFile(data.meta['@mime'] || '') : false;

						return (
							<Flex
								sx={{ borderRadius: '8px', minHeight: '59px', width: '100%' }}
								mt="12px"
								p="12px"
								bg="#F8FAFC"
								key={idx}
							>
								<Flex w="100%" justify="space-between" align="center">
									<Flex align="center">
										<Icon.IconFileText color="#6B7280" size={24} />
										{isPending ? (
											<Loader type="oval" ml="8px" size="20" />
										) : (
											<Stack ml="8px" gap="2px">
												<TFText lh="normal" fw="500" fz="14px">
													{data?.meta['@filename']}
												</TFText>
												<TFText lh="normal" fz="13px" c="#64748B">
													{fmt.toDate(data?.meta['@createdAt'], {
														preset: 'full_date_with_time',
													})}
												</TFText>
											</Stack>
										)}
									</Flex>
									<Box>
										{!decisionWasMade && (
											<ActionIcon mr="2px" variant="transparent" color="red">
												<Icon.IconTrash
													size={22}
													stroke={1.5}
													onClick={async () => {
														if (!data || !detailsId) return;
														removeDetailsAttachmentMutation.mutate({
															formId: detailsId,
															index: idx,
														});
													}}
												/>
											</ActionIcon>
										)}
										{canPreview && data && (
											<PaymentPreviewButton
												fileId={data.id}
												fileName={data.meta['@filename'] || 'file'}
											/>
										)}
										<ActionIcon variant="transparent" color="#2563EB">
											<Icon.IconDownload
												size={22}
												stroke={1.5}
												onClick={async () => {
													if (!data) return;
													await downloadAttachedFile(data.id, data?.meta['@filename']);
												}}
											/>
										</ActionIcon>
									</Box>
								</Flex>
							</Flex>
						);
					})}
				</Box>
			)}
			{!decisionWasMade && (
				<FileButton
					resetRef={resetFileRef}
					onChange={(file) => {
						if (!file) return;
						if (detailsId) {
							updateDetailsAttachmentMutation.mutate({ files: [file], formId: detailsId });
						} else {
							createDetailsAttachmentMutation.mutate({ files: [file], paymentId: paymentId });
						}
					}}
				>
					{(props) => (
						<Stack gap={9}>
							<Button
								mt="1.5rem"
								variant="light"
								fullWidth
								leftSection={
									createDetailsAttachmentMutation.isPending && <Icon.IconUpload size={16} stroke={1.8} />
								}
								disabled={false}
								{...props}
							>
								{createDetailsAttachmentMutation.isPending ? <Loader size={14} /> : 'Upload file'}
							</Button>
						</Stack>
					)}
				</FileButton>
			)}
		</>
	);
};
